<script context="module">

  import ky from "ky";
  import { BASE_URL } from "./config";

  export const api2 = ky.create({
    prefixUrl: BASE_URL,
    timeout: 30000,
    retry: {
      limit: 3,
      methods: ["get", "post", "put", "delete", "patch"],
      statusCodes: [408, 429, 500, 502, 503, 504],
      backoffLimit: 3000,
    },
  });

  export const api = ky.create({
  prefixUrl: BASE_URL,
  timeout: 30000,
  retry: {
    limit: 5, // Increased attempts for resilience
    methods: ["get", "post", "put", "delete", "patch"],
    statusCodes: [408, 429, 500, 502, 503, 504],
    backoffLimit: 5000, // Increase if you want longer waits
  },
  hooks: {
    beforeRetry: [
      ({ request, options, error, retryCount }) => {
        console.error(
          `Retrying ${request.method} ${request.url} (attempt ${retryCount + 1}) due to:`,
          error
        );
      },
    ],
    afterResponse: [
      (request, options, response) => {
        if (!response.ok) {
          if (response.status === 404) {
            console.warn(
              `Server error: ${response.status} ${response.statusText} for ${request.url}`
            );
          } else {
            console.error(
              `API error: ${response.status} ${response.statusText} for ${request.url}`
            );
          }
        }
        return response;
      },
    ],
  },
  throwHttpErrors: false, // Don't throw on non-2xx, let you handle in .json()/.text()
});

  export const apiOutside = ky.create({
    timeout: 30000,
    retry: {
      limit: 3,
      methods: ["get", "post", "put", "delete", "patch"],
      statusCodes: [408, 429, 500, 502, 503, 504],
      backoffLimit: 3000,
    },
  });

  export async function postFile(url = "", params = {}, data = "", contentType = "audio/wave") {
    let urlValue = new URL(url);
    urlValue.search = new URLSearchParams(params).toString();

    // Default options are marked with *
    const response = await fetch(urlValue, {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      headers: {
        "Content-Type": contentType,
      },
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      body: data, // body data type must match "Content-Type" header
    });

    if (response.status >= 400 && response.status < 600) {
      // Enhanced error logging for debugging (TrackJS will pick this up)
      let errorBody = "";
      try {
        errorBody = await response.text();
      } catch (e) {
        errorBody = "Could not read response body";
      }
      console.error("postFile failed:", {
        url: urlValue.toString(),
        status: response.status,
        statusText: response.statusText,
        responseBody: errorBody,
        contentType: contentType,
        headers: Object.fromEntries(response.headers.entries()),
      });
      throw new Error(`Bad response from server status:${response.status} - ${errorBody}`);
    }

    return response.json(); // parses JSON response into native JavaScript objects
  }

  export async function postFileHeaders(
    url = "",
    headers = {},
    params = {},
    data = "",
  ) {
    let urlValue = new URL(url);
    urlValue.search = new URLSearchParams(params).toString();

    let formData = new FormData();
    formData.append("content", data);

    // Default options are marked with *
    const response = await fetch(urlValue, {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      headers: headers,
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      body: data, // body data type must match "Content-Type" header
    });

    return response.json(); // parses JSON response into native JavaScript objects
  }

  export async function postJsonFile(
    url = "",
    headersValue = {},
    params = {},
    data = {},
  ) {
    var urlValue = new URL(url);
    urlValue.search = new URLSearchParams(params).toString();

    // Default options are marked with *
    const response = await fetch(urlValue, {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      headers: headersValue,
      body: JSON.stringify(data), // body data type must match "Content-Type" header
    });

    if (response.status >= 400 && response.status < 600) {
      throw new Error("Bad response from server status:" + response.status);
    }

    return response.json(); // parses JSON response into native JavaScript objects
  }

  export async function postJsonFileNoResponse(
    url = "",
    headersValue = {},
    params = {},
    data = {},
  ) {
    var urlValue = new URL(url);
    urlValue.search = new URLSearchParams(params).toString();

    // Default options are marked with *
    const response = await fetch(urlValue, {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      headers: headersValue,
      body: JSON.stringify(data), // body data type must match "Content-Type" header
    });

    if (response.status >= 400 && response.status < 600) {
      throw new Error("Bad response from server status:" + response.status);
    }

    return response; // parses JSON response into native JavaScript objects
  }

  export async function postJsonFileGetText(
    url = "",
    headersValue = {},
    params = {},
    data = {},
  ) {
    var urlValue = new URL(url);
    urlValue.search = new URLSearchParams(params).toString();

    // Default options are marked with *
    const response = await fetch(urlValue, {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      headers: headersValue,
      body: JSON.stringify(data), // body data type must match "Content-Type" header
    });

    return response.text(); // parses JSON response into native JavaScript objects
  }

  export async function getDataOutside(url = "") {
    const response = await fetch(url, {
      method: "GET", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    });

    if (response.status >= 400 && response.status < 600) {
      throw new Error("Bad response from server status:" + response.status);
    }

    return response.json(); // parses JSON response into native JavaScript objects
  }

  export function generateUUID() {
    // Public Domain/MIT
    let d = new Date().getTime(); //Timestamp
    let d2 = (performance && performance.now && performance.now() * 1000) || 0; //Time in microseconds since page-load or 0 if unsupported
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        let r = Math.random() * 16; //random number between 0 and 16
        if (d > 0) {
          //Use timestamp until depleted
          r = (d + r) % 16 | 0;
          d = Math.floor(d / 16);
        } else {
          //Use microseconds since page-load if supported
          r = (d2 + r) % 16 | 0;
          d2 = Math.floor(d2 / 16);
        }
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      },
    );
  }

  export function setCookie(cname, cvalue, exdays) {
    let d = new Date();
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
    let expires = "expires=" + d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
  }

  export function getCookie(cname) {
    let name = cname + "=";
    let ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }
</script>
